import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { apiPut, parseJsonResponse } from '../api/apiClient';
import { broadcastGutachtenUpdated } from '../utils/gutachtenTabSync';

const DEBOUNCE_MS = 450;

function cloneGutachtenPayload(data) {
  try {
    return structuredClone(data);
  } catch {
    try {
      return JSON.parse(JSON.stringify(data));
    } catch {
      return data;
    }
  }
}

/**
 * Kapselt die PUT-Logik für Gutachten-Updates inkl. SaveIcon und Loading-State.
 * Debounced PUT bei normalen Saves (weniger Race Conditions / weniger Requests).
 * @param {string} gutachtenID
 */
export const useGutachtenPersist = (gutachtenID) => {
  const [saveIcon, setSaveIcon] = useState(false);
  const [saveIcon1, setSaveIcon1] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(false);

  const debounceTimerRef = useRef(null);
  const pendingDataRef = useRef(null);
  const saveIconClearTimerRef = useRef(null);
  const saveIcon1ClearTimerRef = useRef(null);
  const gutachtenIdRef = useRef(gutachtenID);
  gutachtenIdRef.current = gutachtenID;

  const clearSaveIconTimers = () => {
    if (saveIconClearTimerRef.current) {
      clearTimeout(saveIconClearTimerRef.current);
      saveIconClearTimerRef.current = null;
    }
    if (saveIcon1ClearTimerRef.current) {
      clearTimeout(saveIcon1ClearTimerRef.current);
      saveIcon1ClearTimerRef.current = null;
    }
  };

  const runPut = (data, forceReload, forcePageReload) => {
    if (forcePageReload) {
      setIsLoading(true);
    } else {
      setSaveIcon(true);
      setSaveIcon1(true);
    }

    apiPut(`/helper/acl/gutachten/${gutachtenID}`, data)
      .then((response) => parseJsonResponse(response))
      .then(() => {
        broadcastGutachtenUpdated(gutachtenID);
        if (forceReload || forcePageReload) {
          setUpdateTrigger((prev) => !prev);
        }
        if (forcePageReload) {
          setIsLoading(false);
        }
        if (!forcePageReload) {
          clearSaveIconTimers();
          saveIconClearTimerRef.current = setTimeout(() => {
            saveIconClearTimerRef.current = null;
            setSaveIcon(false);
          }, 300);
          saveIcon1ClearTimerRef.current = setTimeout(() => {
            saveIcon1ClearTimerRef.current = null;
            setSaveIcon1(false);
          }, 3000);
        }
      })
      .catch((error) => {
        console.error('Fehler beim Speichern des Gutachtens:', error);
        message.error('Speichern fehlgeschlagen. Bitte erneut versuchen.');
        clearSaveIconTimers();
        setIsLoading(false);
        setSaveIcon(false);
        setSaveIcon1(false);
      });
  };

  /**
   * @param {object} data - dataContent direkt (nicht als { data: ... } gewrapped)
   * @param {boolean} forceReload - Trigger für Re-Fetch der Komponente
   * @param {boolean} forcePageReload - Zeigt LoadingScreen statt SaveIcon
   */
  const updateGutachten = (data, forceReload = false, forcePageReload = false) => {
    if (forceReload || forcePageReload) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingDataRef.current = null;
      runPut(data, forceReload, forcePageReload);
      return;
    }

    pendingDataRef.current = cloneGutachtenPayload(data);
    setSaveIcon(true);
    setSaveIcon1(true);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const payload = pendingDataRef.current;
      pendingDataRef.current = null;
      if (payload) runPut(payload, false, false);
    }, DEBOUNCE_MS);
  };

  useEffect(
    () => () => {
      clearSaveIconTimers();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      const payload = pendingDataRef.current;
      pendingDataRef.current = null;
      if (payload) {
        const id = gutachtenIdRef.current;
        apiPut(`/helper/acl/gutachten/${id}`, payload)
          .then((response) => {
            if (response.ok) {
              broadcastGutachtenUpdated(id);
            }
          })
          .catch(() => {});
      }
    },
    [gutachtenID]
  );

  return {
    saveIcon,
    setSaveIcon,
    saveIcon1,
    setSaveIcon1,
    isLoading,
    setIsLoading,
    updateTrigger,
    setUpdateTrigger,
    updateGutachten,
  };
};
